import { useState } from "react";
import { useForm } from "react-hook-form";

import {
  useErrors,
  useScreen,
  useSignupId,
} from "@auth0/auth0-acul-react/signup-id";

import BeneficiaryLayout from "@/components/BeneficiaryLayout";
import Captcha from "@/components/Captcha/index";
import { ULThemeFloatingLabelField } from "@/components/form/ULThemeFloatingLabelField";
import { ULThemeFormMessage } from "@/components/form/ULThemeFormMessage";
import { Form, FormField, FormItem } from "@/components/ui/form";
import { ULThemeButton } from "@/components/ULThemeButton";
import { ULThemeCheckbox } from "@/components/ULThemeCheckbox";
import { ULThemeAlert, ULThemeAlertTitle } from "@/components/ULThemeError";
import ULThemeLink from "@/components/ULThemeLink";
import { useCaptcha } from "@/hooks/useCaptcha";
import {
  normalizeAuMobile,
  saveSignupDetails,
  signupDetailsAsUlpFields,
} from "@/utils/beneficiary";
import { executeSafely } from "@/utils/helpers/executeSafely";
import { applyCareSuperBrand } from "@/utils/theme/careSuperBrand";
import { applyAuth0Theme } from "@/utils/theme/themeEngine";

// Beneficiary Claims sign-up ("Start a claim") on the shared signup-id screen.
//
// Rendered instead of the member join wizard when the client is the
// Beneficiary Claims SPA (see ../index.tsx). The connection here is the
// passwordless `email` connection: there is no password step. Submitting
// sends the email code and moves to login-passwordless-email-code, and the
// account is created when that code is verified.
//
// Name and mobile are sent as ulp-* fields here AND carried to the code screen
// in sessionStorage (utils/beneficiary.ts), because Auth0 creates the user on
// the code submit, not on this one.
//
// Copy is short and plain on purpose: people starting a claim are often
// writing after a death. No marketing, no "welcome aboard".
interface BeneficiarySignupFormValues {
  givenName: string;
  familyName: string;
  email: string;
  mobile: string;
  captcha: string;
}

const REQUIRED = "This field is required";

function BeneficiarySignupId() {
  const signupId = useSignupId();
  const screen = useScreen();
  const { errors, hasError, dismiss } = useErrors();
  const [privacyConsent, setPrivacyConsent] = useState(false);

  applyAuth0Theme(signupId);
  applyCareSuperBrand();
  document.title = "Start a claim | CareSuper Beneficiary Claims";

  const form = useForm<BeneficiarySignupFormValues>({
    defaultValues: {
      givenName: "",
      familyName: "",
      email: "",
      mobile: "",
      captcha: "",
    },
    reValidateMode: "onBlur",
  });
  const {
    formState: { isSubmitting },
  } = form;

  // Captcha is live on signup-id tenant-wide (Attack Protection), so it applies
  // to this branch too. Same token handling as SignupIdForm: the hook value is
  // the only real token for widget providers.
  const { captchaConfig, captchaProps, captchaValue, isCaptchaSolved } =
    useCaptcha(screen.captcha || undefined, "Enter the code shown above*");
  const captchaRequired = !!screen.isCaptchaAvailable && !!captchaConfig;

  const generalErrors = errors.byType("auth0").filter((err) => !err.field);
  const emailSDKError = errors.byField("email")[0]?.message;
  const captchaSDKError = errors.byField("captcha")[0]?.message;

  const onSubmit = async (data: BeneficiarySignupFormValues) => {
    const mobile = normalizeAuMobile(data.mobile);
    if (!mobile) return; // the field rule already blocks this
    const details = {
      givenName: data.givenName.trim(),
      familyName: data.familyName.trim(),
      mobile,
    };
    saveSignupDetails(details);

    const captcha = captchaValue ?? data.captcha;
    await executeSafely("Beneficiary sign-up", () =>
      signupId.signup({
        email: data.email.trim(),
        ...(screen.isCaptchaAvailable && captcha ? { captcha } : {}),
        ...signupDetailsAsUlpFields(details),
      })
    );
  };

  const textField = (
    name: "givenName" | "familyName" | "email" | "mobile",
    label: string,
    opts: {
      type?: string;
      autoComplete?: string;
      inputMode?: "tel" | "email";
      sdkError?: string;
      validate?: (v: string) => true | string;
    } = {}
  ) => (
    <FormField
      control={form.control}
      name={name}
      rules={{
        required: REQUIRED,
        validate: (v: string) =>
          v.trim() ? (opts.validate ? opts.validate(v) : true) : REQUIRED,
      }}
      render={({ field, fieldState }) => (
        <FormItem>
          <ULThemeFloatingLabelField
            {...field}
            label={label}
            type={opts.type ?? "text"}
            autoComplete={opts.autoComplete}
            inputMode={opts.inputMode}
            error={!!fieldState.error || !!opts.sdkError}
          />
          <ULThemeFormMessage
            sdkError={opts.sdkError}
            hasFormError={!!fieldState.error}
          />
        </FormItem>
      )}
    />
  );

  return (
    <BeneficiaryLayout
      title="Start a claim"
      subtitle="Tell us who you are. We'll email you a code to confirm it's you. You won't need a password."
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
          {hasError && generalErrors.length > 0 && (
            <div className="space-y-3 mb-4">
              {generalErrors.map((error) => (
                <ULThemeAlert
                  key={error.id}
                  variant="destructive"
                  onDismiss={() => dismiss(error.id)}
                >
                  <ULThemeAlertTitle>{error.message}</ULThemeAlertTitle>
                </ULThemeAlert>
              ))}
            </div>
          )}

          {textField("givenName", "First name*", {
            autoComplete: "given-name",
          })}
          {textField("familyName", "Last name*", {
            autoComplete: "family-name",
          })}
          {textField("email", "Email address*", {
            type: "email",
            autoComplete: "email",
            inputMode: "email",
            sdkError: emailSDKError,
            validate: (v) =>
              /^\S+@\S+\.\S+$/.test(v.trim())
                ? true
                : "Enter a valid email address",
          })}
          {textField("mobile", "Mobile number*", {
            type: "tel",
            autoComplete: "tel",
            inputMode: "tel",
            validate: (v) =>
              normalizeAuMobile(v)
                ? true
                : "Enter an Australian mobile number, for example 0412 345 678",
          })}

          <div
            className="flex items-start gap-2 mt-2 mb-2 p-3 rounded-lg"
            style={{ backgroundColor: "#E6F7F1" }}
          >
            <ULThemeCheckbox
              checked={privacyConsent}
              onCheckedChange={(checked) => setPrivacyConsent(!!checked)}
              id="beneficiary-privacy-consent"
            />
            <label
              htmlFor="beneficiary-privacy-consent"
              className="text-sm theme-universal:text-body-text"
            >
              I agree to the{" "}
              <a
                href="#privacy-policy"
                className="underline"
                style={{ color: "#4A44F2" }}
              >
                Privacy Policy
              </a>
              .
            </label>
          </div>

          {screen.isCaptchaAvailable && captchaConfig && (
            <Captcha
              control={form.control}
              name="captcha"
              captcha={captchaConfig}
              {...captchaProps}
              sdkError={captchaSDKError}
              rules={{ required: REQUIRED }}
            />
          )}

          <ULThemeButton
            type="submit"
            className="w-full"
            disabled={
              isSubmitting ||
              !privacyConsent ||
              (captchaRequired && !isCaptchaSolved)
            }
          >
            Continue
          </ULThemeButton>
        </form>
      </Form>

      {screen.loginLink ? (
        <div className="mt-6 text-center">
          <span className="pr-1 text-body-text text-(length:--ul-theme-font-body-text-size) font-body">
            Already started a claim?
          </span>
          <ULThemeLink href={screen.loginLink}>Log in</ULThemeLink>
        </div>
      ) : null}
    </BeneficiaryLayout>
  );
}

export default BeneficiarySignupId;
